/**
 * Email Service
 * 
 * Handles email management operations with real API integration.
 */

import apiClient from './api';

/**
 * Get list of emails with search, filter, and pagination
 * 
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search term for email or name
 * @param {string} params.verificationStatus - Filter by status (verified, pending, failed)
 * @param {number} params.page - Page number (1-indexed)
 * @param {number} params.limit - Items per page
 * @returns {Promise<Object>} - Paginated email list
 */
export const getEmails = async (params = {}) => {
  try {
    const {
      search = '',
      verificationStatus = '',
      page = 1,
      limit = 10
    } = params;

    const response = await apiClient.get('/emails', {
      params: {
        search,
        status: verificationStatus,
        page,
        page_size: limit
      }
    });

    // Transform API response to match frontend format
    // Lambda returns uppercase column names: ID, EMAIL_ADDRESS, NAME, VERIFICATION_STATUS, etc.
    const apiData = response.data?.data || response.data;
    const emailList = apiData?.emails || [];
    const emails = emailList.map(email => ({
      id: email.ID || email.EMAIL_ID,
      email: email.EMAIL_ADDRESS,
      name: email.NAME || email.RECIPIENT_NAME || '',
      verificationStatus: (email.VERIFICATION_STATUS || email.SES_VERIFICATION_STATUS || 'pending').toLowerCase(),
      isActive: email.IS_ACTIVE !== undefined ? email.IS_ACTIVE : true,
      dateAdded: (email.CREATED_AT || email.CREATED_DATE || '')?.split('T')[0] || '',
      lastAttempt: (email.UPDATED_AT || email.LAST_VERIFICATION_ATTEMPT || email.CREATED_AT || '')?.split('T')[0] || ''
    }));

    return {
      data: emails,
      pagination: {
        page: apiData?.pagination?.page || page,
        limit: apiData?.pagination?.page_size || limit,
        total: apiData?.pagination?.total || 0,
        totalPages: apiData?.pagination?.total_pages || 1
      },
      summary: apiData?.summary || null
    };
  } catch (error) {
    console.error('Failed to get emails:', error);
    throw error;
  }
};

/**
 * Get single email by ID
 * 
 * @param {number} emailId - Email ID
 * @returns {Promise<Object>} - Email data
 */
export const getEmailById = async (emailId) => {
  try {
    const response = await apiClient.get(`/emails/${emailId}`);

    const apiData = response.data?.data || response.data;

    // Transform API response to match frontend format
    const email = {
      id: apiData.ID || apiData.EMAIL_ID,
      email: apiData.EMAIL_ADDRESS,
      name: apiData.NAME || apiData.RECIPIENT_NAME || '',
      verificationStatus: (apiData.VERIFICATION_STATUS || apiData.SES_VERIFICATION_STATUS || 'pending').toLowerCase(),
      dateAdded: (apiData.CREATED_AT || apiData.CREATED_DATE || '')?.split('T')[0] || '',
      lastAttempt: (apiData.UPDATED_AT || apiData.LAST_VERIFICATION_ATTEMPT || apiData.CREATED_AT || '')?.split('T')[0] || ''
    };

    return email;
  } catch (error) {
    console.error('Failed to get email:', error);
    throw error;
  }
};

/**
 * Create new email (triggers SES verification)
 * 
 * @param {Object} emailData - Email data
 * @param {string} emailData.email - Email address
 * @param {string} emailData.name - Contact name
 * @returns {Promise<Object>} - Created email
 */
export const createEmail = async (emailData) => {
  try {
    const config = {};
    if (emailData.teamId) {
      config.headers = { 'X-Active-Team-Id': emailData.teamId };
    }

    const response = await apiClient.post('/emails', {
      email_address: emailData.email,
      recipient_name: emailData.name
    }, config);

    const apiData = response.data?.data || response.data;

    // Transform API response to match frontend format
    const newEmail = {
      id: apiData.id || apiData.email_id,
      email: apiData.email_address,
      name: apiData.name || apiData.recipient_name || '',
      verificationStatus: 'active',
      dateAdded: new Date().toISOString().split('T')[0],
      lastAttempt: new Date().toISOString().split('T')[0]
    };

    return { data: newEmail };
  } catch (error) {
    console.error('Failed to create email:', error);
    throw error;
  }
};

/**
 * Update email
 * 
 * @param {number} emailId - Email ID
 * @param {Object} emailData - Updated email data
 * @param {string} emailData.name - Contact name
 * @returns {Promise<Object>} - Updated email
 */
export const updateEmail = async (emailId, emailData) => {
  try {
    await apiClient.put(`/emails/${emailId}`, {
      recipient_name: emailData.name
    });

    // Return success - API doesn't return updated object
    return { 
      data: {
        id: emailId,
        name: emailData.name,
        lastUpdated: new Date().toISOString().split('T')[0]
      }
    };
  } catch (error) {
    console.error('Failed to update email:', error);
    throw error;
  }
};

/**
 * Delete email
 * 
 * @param {number} emailId - Email ID
 * @returns {Promise<void>}
 */
export const deleteEmail = async (emailId) => {
  try {
    await apiClient.delete(`/emails/${emailId}`);
    return { success: true };
  } catch (error) {
    console.error('Failed to delete email:', error);
    throw error;
  }
};

// resendVerification, getUnverifiedEmails, bulkVerifyEmails removed — SES verification flow no longer used (PSE-299)
